import { Block, Floor, ApartmentUnit, Parking, Amenity } from '@/types/resources/property';
import dayjs from 'dayjs';

// Premium Apartment Society: Sunset Gardens
const WORKSPACE_ID = 'demo-workspace';

// Helper function to create workspace reference
const createWorkspaceRef = () => ({
  id: WORKSPACE_ID,
  name: 'Sunset Gardens',
  slug: 'demo-workspace'
});

// Helper function to create activity records
const createActivityRecord = (type: 'created' | 'updated' | 'deleted') => ({
  timestamp: dayjs().subtract(Math.floor(Math.random() * 30), 'days').toDate(),
  type,
  performedBy: {
    id: 'admin-001',
    displayName: 'Property Manager'
  }
});

// Create 10 blocks with uniform, modern naming and proper floor structure
export const blocks: Block[] = [
  {
    id: 'BLK-A',
    name: 'Tower A',
    description: 'Located in the north-east corner of Sunset Gardens, overlooking the main entrance and city skyline',
    floors: Array.from({ length: 20 }, (_, i) => ({
      slug: `tower-a-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower A`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-B',
    name: 'Tower B',
    description: 'Situated in the north-west section, adjacent to the central garden and children\'s play area',
    floors: Array.from({ length: 10 }, (_, i) => ({
      slug: `tower-b-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower B`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-C',
    name: 'Tower C',
    description: 'Central tower positioned near the main clubhouse and swimming pool complex',
    floors: Array.from({ length: 20 }, (_, i) => ({
      slug: `tower-c-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower C`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-D',
    name: 'Tower D',
    description: 'Located in the south-east corner, facing the tennis courts and outdoor sports facilities',
    floors: Array.from({ length: 8 }, (_, i) => ({
      slug: `tower-d-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower D`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-E',
    name: 'Tower E',
    description: 'Positioned in the south-west area, close to the garden and walking track',
    floors: Array.from({ length: 10 }, (_, i) => ({
      slug: `tower-e-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower E`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-F',
    name: 'Tower F',
    description: 'Central tower near the main fountain and party hall, offering premium views of the complex',
    floors: Array.from({ length: 20 }, (_, i) => ({
      slug: `tower-f-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower F`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-G',
    name: 'Tower G',
    description: 'Located in the east wing, adjacent to the library and reading room facilities',
    floors: Array.from({ length: 10 }, (_, i) => ({
      slug: `tower-g-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower G`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-H',
    name: 'Tower H',
    description: 'Situated in the west wing near the spa and wellness center',
    floors: Array.from({ length: 8 }, (_, i) => ({
      slug: `tower-h-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower H`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-I',
    name: 'Tower I',
    description: 'Positioned in the north-central area, close to the gym and fitness center',
    floors: Array.from({ length: 20 }, (_, i) => ({
      slug: `tower-i-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower I`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-J',
    name: 'Tower J',
    description: 'Located in the south-central area, near the BBQ area and indoor games room',
    floors: Array.from({ length: 8 }, (_, i) => ({
      slug: `tower-j-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower J`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  }
];

// Helper function for ordinal suffixes
function getOrdinalSuffix(num: number): string {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

// Create floors array for compatibility (flattened from all blocks)
export const floors: Floor[] = blocks.flatMap(block => block.floors || []);

// Create apartment units for each floor with proper numbering
export const units: ApartmentUnit[] = blocks.flatMap(block => {
  const blockUnits: ApartmentUnit[] = [];
  
  // Get floors for this block
  const blockFloors = block.floors || [];
  
  blockFloors.forEach(floor => {
    // Extract floor number from floor name (e.g., "1st Floor" -> 1)
    const floorNumber = parseInt(floor.name.split(' ')[0]);
    
    // Determine units per floor based on block (some variation for realism)
    let unitsPerFloor = 4; // Default
    if (block.id === 'BLK-A' || block.id === 'BLK-C' || block.id === 'BLK-F' || block.id === 'BLK-I') {
      unitsPerFloor = 6; // 20-floor towers get more units
    } else if (block.id === 'BLK-B' || block.id === 'BLK-E' || block.id === 'BLK-G') {
      unitsPerFloor = 5; // 10-floor towers get medium units
    } else {
      unitsPerFloor = 4; // 8-floor towers get fewer units
    }
    
    for (let unitNum = 1; unitNum <= unitsPerFloor; unitNum++) {
      const unitTypes = ['1BHK', '2BHK', '3BHK', '4BHK'];
      const unitType = unitTypes[Math.floor(Math.random() * unitTypes.length)];
      const area = unitType === '1BHK' ? 650 : unitType === '2BHK' ? 950 : unitType === '3BHK' ? 1250 : 1600;
      
      // Create proper unit number (e.g., 101, 102, 201, 202)
      const unitNumber = `${floorNumber}${unitNum.toString().padStart(2, '0')}`;
      
      blockUnits.push({
        id: `${block.id}-${unitNumber}`,
        number: unitNumber,
        bedrooms: parseInt(unitType[0]),
        bathrooms: Math.floor(Math.random() * 2) + 1,
        area: area,
        status: Math.random() > 0.2 ? 'occupied' : 'vacant',
        block: {
          id: block.id,
          name: block.name
        },
        floor: {
          name: floor.name,
          slug: floor.slug
        },
        description: `${unitType} apartment on ${floor.name} of ${block.name}`,
        workspace: createWorkspaceRef(),
        activityRecords: [createActivityRecord('created')]
      });
    }
  });
  
  return blockUnits;
});

// Create parking spots for all units
export const parkings: Parking[] = units.map((unit, index) => {
  const parkingTypes = ['car', 'bike', 'other'];
  const parkingType = parkingTypes[Math.floor(Math.random() * parkingTypes.length)] as 'car' | 'bike' | 'other';
  
  return {
    id: `PARK-${(index + 1).toString().padStart(3, '0')}`,
    number: `P${(index + 1).toString().padStart(3, '0')}`,
    type: parkingType,
    status: unit.status === 'occupied' ? 'occupied' : 'available',
    apartmentUnit: unit.status === 'occupied' ? {
      id: unit.id,
      number: unit.number
    } : undefined,
    block: unit.block,
    pricing: {
      amount: parkingType === 'car' ? 100 : parkingType === 'bike' ? 50 : 75,
      currency: 'USD',
      period: 'monthly'
    },
    description: `${parkingType} parking spot for ${unit.block?.name}`,
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  };
});

// Create comprehensive amenities for a premium apartment society
export const amenities: Amenity[] = [
  {
    id: 'AMEN-001',
    name: 'Swimming Pool',
    type: 'swimming_pool',
    description: 'Olympic-size swimming pool with temperature control',
    capacity: 50,
    status: 'active',
    operatingHours: {
      open: '06:00',
      close: '22:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-C',
      name: 'Tower C'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-002',
    name: 'Gym & Fitness Center',
    type: 'gym',
    description: 'Fully equipped gym with modern equipment and personal trainers',
    capacity: 30,
    status: 'active',
    operatingHours: {
      open: '05:00',
      close: '23:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-A',
      name: 'Tower A'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-003',
    name: 'Clubhouse',
    type: 'community_hall',
    description: 'Multi-purpose clubhouse for events and gatherings',
    capacity: 200,
    status: 'active',
    operatingHours: {
      open: '08:00',
      close: '23:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-B',
      name: 'Tower B'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-004',
    name: 'Tennis Court',
    type: 'other',
    description: 'Professional tennis court with floodlights',
    capacity: 4,
    status: 'active',
    operatingHours: {
      open: '06:00',
      close: '22:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-D',
      name: 'Tower D'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-005',
    name: 'Children\'s Play Area',
    type: 'playground',
    description: 'Safe and fun play area for children with modern equipment',
    capacity: 20,
    status: 'active',
    operatingHours: {
      open: '06:00',
      close: '20:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-E',
      name: 'Tower E'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-006',
    name: 'Party Hall',
    type: 'community_hall',
    description: 'Elegant party hall for celebrations and events',
    capacity: 150,
    status: 'active',
    operatingHours: {
      open: '10:00',
      close: '24:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-F',
      name: 'Tower F'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-007',
    name: 'Library & Reading Room',
    type: 'other',
    description: 'Quiet library with extensive book collection and study areas',
    capacity: 25,
    status: 'active',
    operatingHours: {
      open: '08:00',
      close: '22:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-G',
      name: 'Tower G'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-008',
    name: 'Spa & Wellness Center',
    type: 'other',
    description: 'Relaxing spa with massage therapy and wellness treatments',
    capacity: 10,
    status: 'active',
    operatingHours: {
      open: '09:00',
      close: '21:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-H',
      name: 'Tower H'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-009',
    name: 'BBQ Area',
    type: 'other',
    description: 'Outdoor BBQ area with seating and cooking facilities',
    capacity: 15,
    status: 'active',
    operatingHours: {
      open: '16:00',
      close: '23:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-I',
      name: 'Tower I'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-010',
    name: 'Indoor Games Room',
    type: 'other',
    description: 'Games room with table tennis, carrom, and board games',
    capacity: 20,
    status: 'active',
    operatingHours: {
      open: '08:00',
      close: '22:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-J',
      name: 'Tower J'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-011',
    name: 'Yoga & Meditation Hall',
    type: 'other',
    description: 'Peaceful space for yoga, meditation, and wellness classes',
    capacity: 30,
    status: 'active',
    operatingHours: {
      open: '06:00',
      close: '21:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-A',
      name: 'Tower A'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-012',
    name: 'Garden & Walking Track',
    type: 'garden',
    description: 'Beautiful garden with walking track and seating areas',
    capacity: 100,
    status: 'active',
    operatingHours: {
      open: '00:00',
      close: '24:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-E',
      name: 'Tower E'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  }
];

// Automatically generate block stats from the units, parkings, amenities arrays
export const mockBlockStats: { [blockId: string]: {
  totalApartmentUnits: number;
  totalParkings: number;
  totalAmenities: number;
  totalResidents: number;
} } = blocks.reduce((acc: { [blockId: string]: any }, block) => {
  const blockId = block.id;
  const blockUnits = units.filter(u => u.block?.id === blockId);
  const blockParkings = parkings.filter(p => p.block?.id === blockId);
  const blockAmenities = amenities.filter(a => a.block?.id === blockId);
  
  // Calculate residents based on occupied units (about 80% occupancy)
  const occupiedUnits = blockUnits.filter(unit => unit.status === 'occupied');
  const totalResidents = occupiedUnits.length;
  
  acc[blockId] = {
    totalApartmentUnits: blockUnits.length,
    totalParkings: blockParkings.length,
    totalAmenities: blockAmenities.length,
    totalResidents: totalResidents
  };
  return acc;
}, {});
